<script setup lang="ts">
// The optional "where are you playing?" step between the confirm tap (place
// bet / enter) and the request — BMO play only. Play at home (site 1) is always
// one tap away and needs no location; venues appear only after the player taps
// "share my location" (browser Geolocation, or the native bridge in the embed).
// Selection rules (user-set): exactly ONE venue in range → auto-select, no tap;
// several → list them above home and wait; none → up to 5 nearby venues with
// distances as a hint, the player continues with home. The parent owns what
// happens next (remember the choice for the TTL, then submit).
//
// Never SSR-rendered (it opens from a click) and teleported to <body>: the
// scoreboard's slip modal has backdrop-filter, which traps position:fixed
// descendants, and the sticky slips sit at z-index 15 UNDER the header — an
// inline overlay would render clipped or behind. The teleport is gated on
// mount exactly like FilterTabs so it can never run during hydration.
import type { VenuePurpose, VenueSite } from '~/composables/useVenueLookup'
import { useLocationHelp } from '~/composables/useLocationHelp'

const props = defineProps<{ purpose: VenuePurpose }>()
const emit = defineEmits<{
  (e: 'select', siteId: number, name: string): void
  (e: 'close'): void
}>()

const bridge = useNativeBridge()
const lookup = useVenueLookup()
// module-scoped refs, created once — destructuring keeps them reactive and
// lets the template read them unwrapped
const { status, inRange, nearby, home, deniedForever, reasonCodes, seq } = lookup
const help = useLocationHelp(deniedForever)

const isMounted = ref(false)
// the choice this instance made (tap or auto-select): shown as a brief
// confirmation while the parent proceeds, and a guard against a double tap
const chosen = ref<{ siteId: number; name: string } | null>(null)

const busy = computed(() => status.value === 'locating' || status.value === 'checking')
const rateLimited = computed(() => reasonCodes.value.includes('RATE_LIMITED'))
const hasLocationCap = computed(() => !!bridge.ctx.value.capabilities?.includes('location'))
// native shell too old to serve the bridge location request AND the WebView's
// own geolocation failed -> the fix is an app update, not a settings toggle
const needsAppUpdate = computed(() =>
  bridge.isNative.value && !hasLocationCap.value && status.value === 'location_needed')
const openSettings = computed(() => bridge.isNative.value && deniedForever.value)

// a picker opened onto a stale non-result (yesterday's denied permission, an
// old error) starts from the idle prompt; a fresh in_venue/no_venue result is
// shown as-is — the player already shared within the TTL
if (!busy.value && !lookup.isFresh()) lookup.reset()

// auto-select: exactly one venue in range → pick it without a tap. Fires only
// for a lookup that COMPLETES while this picker is open (seq moves past the
// value captured at open) and at most once (chosen) — so a reopened picker
// showing the same fresh result never re-fires, and "change" really offers one.
const openedSeq = seq.value
watch(seq, (s) => {
  if (s <= openedSeq || chosen.value) return
  if (status.value === 'in_venue' && inRange.value.length === 1) {
    const v = inRange.value[0]
    choose(v.site_id, v.name)
  }
})

function choose(siteId: number, name: string) {
  if (chosen.value) return
  chosen.value = { siteId, name }
  emit('select', siteId, name)
}
function pickVenue(v: VenueSite) { choose(v.site_id, v.name) }
function pickHome() { choose(home.value.site_id, home.value.name) }
function run() { lookup.lookup(props.purpose) }

const confirmText = computed(() => {
  const c = chosen.value
  if (!c) return ''
  return c.siteId > 1 ? `playing at ${c.name} ✓` : `${c.name} ✓`
})

let offBack: (() => void) | null = null
onMounted(() => {
  isMounted.value = true   // safe to teleport now (post-hydration)
  // OS back closes the picker before anything under it (newest interceptor runs first)
  offBack = bridge.registerBack(() => { emit('close'); return true })
})
onBeforeUnmount(() => { offBack?.(); offBack = null })
</script>

<template>
  <Teleport to="body" :disabled="!isMounted">
    <!-- rendered only once mounted: the pre-teleport paint would otherwise flash
         inline inside the slip for a frame before moving to <body> -->
    <div v-if="isMounted" class="vp-overlay" @click.self="emit('close')">
      <div class="vp-card" role="dialog" aria-modal="true" aria-label="where are you playing?">
        <button class="vp-x" aria-label="close" @click="emit('close')">✕</button>

        <!-- chosen (tap or auto-select): brief confirmation while the parent proceeds -->
        <div v-if="chosen" class="state">
          <div class="badge ok">✓</div>
          <p class="tag">{{ confirmText }}</p>
        </div>

        <!-- locating / checking -->
        <div v-else-if="busy" class="state">
          <div class="spin" aria-hidden="true" />
          <p class="tag">{{ status === 'locating' ? 'getting your location…' : 'finding venues near you…' }}</p>
        </div>

        <!-- idle: home is one tap; venues need the share tap (the browser prompt
             stays attributed to that gesture) -->
        <div v-else-if="status === 'idle'" class="state">
          <div class="badge geo">◎</div>
          <p class="tag">where are you playing?</p>
          <p class="lede">Playing at a venue may earn you other prizes or rewards, not just BMO</p>
          <div class="rows">
            <button class="row home" @click="pickHome">
              <span class="vn">{{ home.name }}</span><span class="vd">→</span>
            </button>
          </div>
          <button class="btn-primary" @click="run">share my location</button>
        </div>

        <!-- in_venue / no_venue -->
        <div v-else-if="status === 'in_venue' || status === 'no_venue'" class="state">
          <div class="badge geo">◎</div>
          <template v-if="inRange.length">
            <p class="tag">choose where you're playing</p>
            <div class="rows">
              <button v-for="v in inRange" :key="v.site_id" class="row" @click="pickVenue(v)">
                <span class="vn">{{ v.name }}</span><span class="vd">{{ v.distance_mi.toFixed(1) }} mi</span>
              </button>
              <button class="row home" @click="pickHome">
                <span class="vn">{{ home.name }}</span><span class="vd">→</span>
              </button>
            </div>
            <!-- the rest, display-only: out of range, so not selectable -->
            <details v-if="nearby.length" class="more">
              <summary>show more locations</summary>
              <div class="rows display">
                <div v-for="v in nearby" :key="v.site_id" class="row static">
                  <span class="vn">{{ v.name }}</span><span class="vd">{{ v.distance_mi.toFixed(1) }} mi</span>
                </div>
              </div>
            </details>
          </template>
          <template v-else>
            <!-- no venue in range: the nearby list is the whole hint (no accordion, no copy) -->
            <p class="tag">{{ nearby.length ? 'nearby venues' : 'no venues nearby' }}</p>
            <div v-if="nearby.length" class="rows display">
              <div v-for="v in nearby" :key="v.site_id" class="row static">
                <span class="vn">{{ v.name }}</span><span class="vd">{{ v.distance_mi.toFixed(1) }} mi</span>
              </div>
            </div>
            <div class="rows">
              <button class="row home" @click="pickHome">
                <span class="vn">{{ home.name }}</span><span class="vd">→</span>
              </button>
            </div>
          </template>
          <button class="btn-link" @click="run">refresh location</button>
        </div>

        <!-- location_needed: recoverable — permission / no fix. home stays open -->
        <div v-else-if="status === 'location_needed'" class="state">
          <div class="badge warn">◎</div>
          <p class="tag">turn on location to see venues near you</p>
          <p v-if="needsAppUpdate" class="note">Your Buzztime app version can't share location yet —
            update the app from the store, then try again.</p>
          <details v-else class="help">
            <summary>how to enable location ({{ help.label }})</summary>
            <ol><li v-for="s in help.steps" :key="s">{{ s }}</li></ol>
          </details>
          <div class="rows">
            <button class="row home" @click="pickHome">
              <span class="vn">{{ home.name }}</span><span class="vd">→</span>
            </button>
          </div>
          <button v-if="openSettings" class="btn-primary" @click="lookup.openSettings()">open settings</button>
          <button class="btn-primary" :class="{ secondary: openSettings }" @click="run">try again</button>
        </div>

        <!-- spoof_suspected / error / rate-limited: one line, home stays open -->
        <div v-else class="state">
          <div class="badge warn">!</div>
          <p class="tag">{{ status === 'spoof_suspected'
            ? 'we couldn’t verify your location — venue play is off for now'
            : rateLimited ? 'too many location checks — wait a minute'
            : 'location service is unavailable — try again shortly' }}</p>
          <div class="rows">
            <button class="row home" @click="pickHome">
              <span class="vn">{{ home.name }}</span><span class="vd">→</span>
            </button>
          </div>
          <button class="btn-link" @click="run">try again</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
/* scrim + card copied from LocationGate's overlay form, one layer higher: above
   the scoreboard slip modal (60), the cash gate (90) and the header (20) */
.vp-overlay { position: fixed; inset: 0; z-index: 100; display: grid; place-items: center;
  background: rgba(0, 0, 0, 0.55); padding: 20px;
  padding-bottom: max(20px, var(--bt-bottom-safe, 0px)); }
.vp-card { width: 100%; max-width: 420px; margin-inline: auto; position: relative;
  background: var(--panel); border: 1px solid var(--line); border-radius: 16px; padding: 28px; }
.vp-x { position: absolute; top: 10px; right: 12px; background: none; color: var(--muted); font-size: 15px; }
.vp-x:hover { color: var(--ink); }
.state { text-align: center; padding: 8px 0; }
.tag { color: var(--ink); font-size: 14px; font-weight: 700; margin: 8px 0 12px; }
.lede { color: var(--muted); font-size: 13px; line-height: 1.5; margin: 0 auto 16px; max-width: 320px; }
.note { color: var(--muted); font-size: 11px; margin-top: 10px; font-family: var(--font-mono); line-height: 1.5; }
.badge { width: 52px; height: 52px; margin: 6px auto 4px; display: grid; place-items: center;
  border-radius: 50%; font-size: 20px; font-weight: 800; font-family: var(--font-mono); }
.badge.ok { background: color-mix(in srgb, var(--accent) 18%, transparent); color: var(--accent); }
.badge.warn { background: color-mix(in srgb, var(--brand) 14%, transparent); color: var(--brand); }
.badge.geo { background: color-mix(in srgb, var(--brand) 14%, transparent); color: var(--brand); }
.btn-primary { width: 100%; margin-top: 14px; color: #fff; padding: 13px; font-size: 15px;
  font-weight: 800; border-radius: 10px; text-align: center;
  background: linear-gradient(90deg, var(--brand) 0%, var(--brand-2) 100%);
  box-shadow: 0 6px 18px rgba(219, 86, 152, 0.28); }
.btn-primary.secondary { background: var(--panel-2); color: var(--ink); box-shadow: none;
  border: 1px solid var(--line); }
.btn-link { width: 100%; background: none; color: var(--muted); font-size: 12px; margin-top: 12px; }
.btn-link:hover { color: var(--ink); }

/* venue rows: the in-range set + home are buttons; out-of-range rows are static */
.rows { text-align: left; margin: 2px auto 0; max-width: 320px;
  border: 1px solid var(--line); border-radius: 12px; background: var(--panel-2); overflow: hidden; }
.row { display: flex; align-items: center; gap: 11px; width: 100%; padding: 11px 12px;
  text-align: left; color: inherit; border-bottom: 1px solid rgba(53, 42, 96, 0.5); }
.row:last-child { border-bottom: none; }
button.row:hover { background: rgba(255, 255, 255, 0.04); }
.row.static { cursor: default; }
.row.home .vn { color: var(--accent); }
.vn { flex: 1; font-size: 13px; font-weight: 600; color: var(--ink); }
.vd { color: var(--muted); font-size: 12px; font-family: var(--font-mono); white-space: nowrap; }
.rows.display .row { padding: 9px 12px; }
.more { text-align: left; margin: 10px auto 0; max-width: 320px; color: var(--muted); font-size: 12px; }
.more summary { cursor: pointer; color: var(--muted); font-size: 12px; margin-bottom: 6px; }
.more summary:hover { color: var(--ink); }
.more .rows { margin-top: 6px; }
.help { text-align: left; margin: 6px auto 12px; max-width: 320px; color: var(--muted); font-size: 12px; }
.help summary { cursor: pointer; color: var(--ink); font-size: 12px; margin-bottom: 6px; }
.help ol { margin: 6px 0 0 18px; display: grid; gap: 4px; line-height: 1.45; }
.spin { width: 28px; height: 28px; margin: 10px auto; border: 3px solid var(--line);
  border-top-color: var(--brand); border-radius: 50%; animation: sp 0.9s linear infinite; }
@keyframes sp { to { transform: rotate(360deg); } }
</style>
